/**
 * Fish species definitions
 * Phase 1: Mountain Lake fish
 * Ranges based on real-world data for mountain lake fishing
 */

import { FishDefinition, FishRarity } from '@/types';

/**
 * All fish species in the game
 * Ranges based on real-world data for mountain lake fishing
 */
export const FISH_DEFINITIONS: FishDefinition[] = [
  // === COMMON FISH ===
  {
    id: 'bluegill',
    name: 'Bluegill',
    baseValue: 5,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    lengthRange: [4, 12], // inches (typical 6-8", max 12")
    weightRange: [4, 32], // ounces (typical 8 oz, max 2 lbs)
    description: 'A small, common sunfish with blue-tinted gills.',
    icon: '🐟',
    emoji: '🐟',
  },
  {
    id: 'perch',
    name: 'Yellow Perch',
    baseValue: 6,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    lengthRange: [4, 12], // inches (typical 6-10", max 12")
    weightRange: [4, 20], // ounces (typical 4-12 oz, max 1.25 lbs)
    description: 'Striped panfish, abundant in mountain lakes.',
    icon: '🐟',
    emoji: '🐟',
  },
  {
    id: 'brook_trout',
    name: 'Brook Trout',
    baseValue: 8,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    lengthRange: [10, 26], // inches (typical 12-16", max 26")
    weightRange: [16, 96], // ounces (typical 1-5 lbs, max 6 lbs)
    description: 'Beautiful native trout with distinctive vermiculations.',
    icon: '🐟',
    emoji: '🐟',
  },
  {
    id: 'cutthroat_trout',
    name: 'Cutthroat Trout',
    baseValue: 10,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    lengthRange: [8, 22], // inches (typical 12-18", max 22")
    weightRange: [8, 96], // ounces (typical 0.5-5 lbs, max 6 lbs)
    canBeHatchery: true, // Can be wild or hatchery-raised
    description: 'Native trout with distinctive red slash marks under jaw.',
    icon: '🐠',
    emoji: '🐠',
  },

  // === UNCOMMON FISH ===
  {
    id: 'trout',
    name: 'Rainbow Trout',
    baseValue: 12,
    rarity: FishRarity.UNCOMMON,
    biome: 'lake',
    lengthRange: [10, 24], // inches (typical 12-20", max 24" for lake dwelling)
    weightRange: [12, 96], // ounces (typical 1-5 lbs, max 6 lbs for lake)
    canBeHatchery: true, // Can be wild or hatchery-raised
    description: 'Colorful trout prized by anglers.',
    icon: '🐠',
    emoji: '🐠',
  },
  {
    id: 'brown_trout',
    name: 'Brown Trout',
    baseValue: 15,
    rarity: FishRarity.UNCOMMON,
    biome: 'lake',
    lengthRange: [8, 24], // inches (typical 14-24", max 24")
    weightRange: [32, 160], // ounces (typical 2-8 lbs, max 10 lbs)
    canBeHatchery: true, // Can be wild or hatchery-raised
    description: 'Wily European import known for wariness and size.',
    icon: '🐠',
    emoji: '🐠',
  },

  // === RARE FISH ===
  {
    id: 'bass',
    name: 'Largemouth Bass',
    baseValue: 25,
    rarity: FishRarity.RARE,
    biome: 'lake',
    lengthRange: [8, 24], // inches (typical 10-18", trophy 20"+)
    weightRange: [12, 160], // ounces (typical 1-5 lbs, max 10 lbs)
    description: 'A powerful predator fish, challenging to catch.',
    icon: '🐡',
    emoji: '🐡',
  },
  {
    id: 'cuttbow_trout',
    name: 'Cuttbow Trout',
    baseValue: 28,
    rarity: FishRarity.RARE,
    biome: 'lake',
    lengthRange: [13, 30], // inches (typical 15-20", max 30")
    weightRange: [16, 240], // ounces (typical 1-8 lbs, max 15 lbs)
    description: 'Rare hybrid of cutthroat and rainbow trout with combined traits.',
    icon: '🐠',
    emoji: '🐠',
  },
  {
    id: 'tiger_trout',
    name: 'Tiger Trout',
    baseValue: 30,
    rarity: FishRarity.RARE,
    biome: 'lake',
    lengthRange: [10, 25], // inches (typical 12-18", max 25")
    weightRange: [32, 80], // ounces (typical 2-5 lbs, max 5 lbs)
    description: 'Striking hybrid of brook and brown trout with tiger-like markings.',
    icon: '🐟',
    emoji: '🐟',
  },

  // === EPIC FISH (Very Rare) ===
  {
    id: 'arctic_grayling',
    name: 'Arctic Grayling',
    baseValue: 70,
    rarity: FishRarity.EPIC,
    biome: 'lake',
    lengthRange: [12, 20], // inches (typical 13-16", max 20")
    weightRange: [32, 64], // ounces (typical 2-4 lbs, max 4 lbs)
    description: 'Magnificent fish with sail-like dorsal fin, extremely rare.',
    icon: '🐟',
    emoji: '🐟',
  },

  // === TRAP CREATURES — Mountain Lake ===
  {
    id: 'crawdad',
    name: 'Crawdad',
    baseValue: 4,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    category: 'crustacean',
    lengthRange: [2, 5],    // inches (small freshwater crayfish)
    weightRange: [1, 4],    // ounces
    description: 'A small freshwater crayfish found in rocky lake bottoms.',
    icon: '🦞',
    emoji: '🦞',
  },
  {
    id: 'signal_crayfish',
    name: 'Signal Crayfish',
    baseValue: 9,
    rarity: FishRarity.UNCOMMON,
    biome: 'lake',
    category: 'crustacean',
    lengthRange: [4, 8],    // inches (larger crayfish species)
    weightRange: [2, 10],   // ounces
    description: 'A robust crayfish with distinctive white-tipped claws.',
    icon: '🦞',
    emoji: '🦞',
  },
  {
    id: 'rusty_crayfish',
    name: 'Rusty Crayfish',
    baseValue: 18,
    rarity: FishRarity.RARE,
    biome: 'lake',
    category: 'crustacean',
    lengthRange: [3, 6],    // inches
    weightRange: [2, 8],    // ounces
    description: 'An aggressive crayfish with rust-colored spots on its claws.',
    icon: '🦞',
    emoji: '🦞',
  },
  {
    id: 'freshwater_mussel',
    name: 'Freshwater Mussel',
    baseValue: 7,
    rarity: FishRarity.COMMON,
    biome: 'lake',
    category: 'shellfish',
    lengthRange: [2, 6],    // inches (shell length)
    weightRange: [2, 12],   // ounces
    description: 'A filter-feeding bivalve prized for its pearly shell interior.',
    icon: '🐚',
    emoji: '🐚',
  },
];

/**
 * Fish lookup by ID (for fast access)
 */
const FISH_MAP = new Map(FISH_DEFINITIONS.map((fish) => [fish.id, fish]));

/**
 * Get fish definition by ID
 * @throws Error if fish not found
 */
export function getFishDefinition(id: string): FishDefinition {
  const fish = FISH_MAP.get(id);
  if (!fish) {
    throw new Error(`Fish definition not found: ${id}`);
  }
  return fish;
}

/**
 * Get all fish for a specific biome
 */
export function getFishByBiome(biome: string): FishDefinition[] {
  return FISH_DEFINITIONS.filter((f) => f.biome === biome);
}

/**
 * Get all fish of a specific rarity
 */
export function getFishByRarity(rarity: FishRarity): FishDefinition[] {
  return FISH_DEFINITIONS.filter((f) => f.rarity === rarity);
}

/**
 * Check if a fish ID exists
 */
export function fishExists(id: string): boolean {
  return FISH_MAP.has(id);
}

/**
 * Alias for getFishDefinition (for backward compatibility)
 */
export const getFishById = getFishDefinition;
